// Edit Existing Template flow v2 per the 2026-06-01 design doc.
//
// One guided path: pick a template from the library, download the original,
// edit it locally in Word, drop the edited file back in, fill in the form,
// submit. No "Upload a file" escape hatch — anything that genuinely is not
// in the library belongs in the New Template wizard.
//
// Submit is intentionally not hard-disabled per James's review comment ("no
// disabled until" — the interface should pull the user toward the right
// path). If they submit without an edited file uploaded, we show a soft
// nudge inline rather than blocking the click.
import ExistingTemplatePicker from "./documentMapper/ExistingTemplatePicker";
import {
  CheckCircleRounded as CheckIcon,
  CloudDoneRounded as CloudDoneIcon,
  CloudUploadRounded as UploadIcon,
  DownloadRounded as DownloadIcon,
  EditNoteRounded as AnnotateIcon,
  SendRounded as SubmitIcon,
} from "@mui/icons-material";
import { PriorityHighRounded as PriorityIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import * as logger from "common/logger";
import Button from "components/common/Button";
import * as fileUtils from "fileUtils";
import { useRef, useState } from "react";

const downloadOriginalToBrowser = async (templateTitle, s3FileId) => {
  const url = await fileUtils.getDownloadUrl(s3FileId);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download template (${response.status})`);
  }
  const blob = await response.blob();
  const filename = `${templateTitle || "template"}.docx`;
  const anchorUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = anchorUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(anchorUrl);
};

const EditExistingTemplateFlow = ({
  onSubmitRequest,
  onCancel,
  // Optional: when supplied, the "Annotate this template" affordance is
  // rendered next to the template-selected card. Invoked with the picked
  // template ({ file, s3FileId, templateId, templateTitle }) so the parent
  // can route into the mapper canvas for annotation-based requests.
  onAnnotateInstead,
}) => {
  const theme = useTheme();
  const [picked, setPicked] = useState(null);
  const [downloadingOriginal, setDownloadingOriginal] = useState(false);
  const [editedFile, setEditedFile] = useState(null);
  const [editedFileId, setEditedFileId] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("idle");
  const [showMissingFileNudge, setShowMissingFileNudge] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("MEDIUM");

  const fileInputRef = useRef(null);

  const handleTemplatePicked = (selection) => {
    setPicked(selection);
    setTitle(selection.templateTitle ?? "");
    setEditedFile(null);
    setEditedFileId(null);
    setUploadStatus("idle");
    setShowMissingFileNudge(false);
  };

  const handleDownloadOriginal = async () => {
    if (!picked) return;
    setDownloadingOriginal(true);
    try {
      await downloadOriginalToBrowser(picked.templateTitle, picked.s3FileId);
    } catch (error) {
      logger.exception(error, {
        breadcrumb: { action: "download-original" },
      });
    } finally {
      setDownloadingOriginal(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target?.files?.[0] || e.dataTransfer?.files?.[0];
    if (!file) return;
    setEditedFile(file);
    setEditedFileId(null);
    setUploadStatus("uploading");
    setShowMissingFileNudge(false);
    try {
      const fileId = await fileUtils.createFileEntry(file, false);
      setEditedFileId(fileId);
      setUploadStatus("uploaded");
    } catch (error) {
      logger.exception(error, { breadcrumb: { action: "upload-edited" } });
      setUploadStatus("error");
    }
    if (e.target) e.target.value = null;
  };

  const submit = async () => {
    if (!picked) return;
    setSubmitting(true);
    try {
      await onSubmitRequest({
        templateId: picked.templateId,
        title:
          title?.trim() ||
          `Template Edit: ${picked.templateTitle ?? picked.templateId}`,
        description: description?.trim() || "",
        priority,
        fileName: editedFile?.name || `${picked.templateTitle}.docx`,
        s3FileId: editedFileId,
        fileType:
          editedFile?.type ||
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        placements: [],
        annotations: [],
        customFields: [],
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitClick = () => {
    if (!editedFileId) {
      setShowMissingFileNudge(true);
      return;
    }
    submit();
  };

  if (!picked) {
    return (
      <Stack gap={3}>
        <Box>
          <Typography variant={"h6"} fontWeight={600}>
            {"Which template would you like to edit?"}
          </Typography>
          <Typography variant={"body2"} color={"text.secondary"}>
            {
              "Pick the template from your library. You will download a copy, edit it in Word, and upload the edited version back in the next step."
            }
          </Typography>
        </Box>
        <ExistingTemplatePicker onSelect={handleTemplatePicked} />
        {onCancel && (
          <Box>
            <Button variant={"text"} onClick={onCancel}>
              {"Cancel"}
            </Button>
          </Box>
        )}
      </Stack>
    );
  }

  return (
    <Stack gap={3}>
      <Paper variant={"outlined"} sx={{ p: 2 }}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Stack direction={"row"} alignItems={"center"} gap={1.5}>
            <CheckIcon color={"success"} />
            <Box>
              <Typography variant={"subtitle1"} fontWeight={600}>
                {picked.templateTitle}
              </Typography>
              <Typography variant={"caption"} color={"text.secondary"}>
                {"Template selected"}
              </Typography>
            </Box>
          </Stack>
          <Button
            variant={"text"}
            onClick={() => {
              setPicked(null);
              setEditedFile(null);
              setEditedFileId(null);
              setUploadStatus("idle");
            }}
          >
            {"Pick a different template"}
          </Button>
        </Stack>
      </Paper>

      <Paper variant={"outlined"} sx={{ p: 2 }}>
        <Stack gap={1.5}>
          <Box>
            <Typography variant={"subtitle1"} fontWeight={600}>
              {"1. Download the original"}
            </Typography>
            <Typography variant={"body2"} color={"text.secondary"}>
              {
                "Open it in Word, make your edits, and save it locally. Then come back to upload your edited version."
              }
            </Typography>
          </Box>
          <Box>
            <Button
              startIcon={<DownloadIcon />}
              variant={"contained"}
              onClick={handleDownloadOriginal}
              isLoading={downloadingOriginal}
            >
              {"Download Original"}
            </Button>
          </Box>
        </Stack>
      </Paper>

      <Paper variant={"outlined"} sx={{ p: 2 }}>
        <Stack gap={1.5}>
          <Box>
            <Typography variant={"subtitle1"} fontWeight={600}>
              {"2. Upload your edited version"}
            </Typography>
            <Typography variant={"body2"} color={"text.secondary"}>
              {
                "Drag the edited .docx in below, or browse for it. We will send this file alongside your request so the support team can apply your changes."
              }
            </Typography>
          </Box>
          <Paper
            variant={"outlined"}
            sx={{
              p: 3,
              textAlign: "center",
              cursor: "pointer",
              borderStyle: "dashed",
              borderWidth: 2,
              borderColor:
                uploadStatus === "uploaded"
                  ? theme.palette.success.main
                  : theme.palette.divider,
              backgroundColor:
                uploadStatus === "uploaded"
                  ? `${theme.palette.success.main}08`
                  : "transparent",
            }}
            onClick={() => fileInputRef.current?.click()}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleFileSelect(e);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {uploadStatus === "uploaded" ? (
              <Stack
                direction={"row"}
                gap={1}
                alignItems={"center"}
                justifyContent={"center"}
              >
                <CloudDoneIcon color={"success"} />
                <Typography>
                  {editedFile?.name ?? "Edited file uploaded"}
                </Typography>
              </Stack>
            ) : uploadStatus === "uploading" ? (
              <Stack
                direction={"row"}
                gap={1}
                alignItems={"center"}
                justifyContent={"center"}
              >
                <CircularProgress size={20} />
                <Typography>{"Uploading..."}</Typography>
              </Stack>
            ) : (
              <Stack
                alignItems={"center"}
                gap={1}
                sx={{ color: "text.secondary" }}
              >
                <UploadIcon sx={{ fontSize: 40 }} />
                <Typography variant={"body2"}>
                  {"Drop your edited .docx here or click to browse"}
                </Typography>
              </Stack>
            )}
            <input
              ref={fileInputRef}
              type={"file"}
              hidden
              accept={".docx,.doc,.pdf"}
              onChange={handleFileSelect}
              data-testid={"edited-file-input"}
            />
          </Paper>
          {uploadStatus === "error" && (
            <Alert severity={"error"}>
              {"Could not upload that file. Please try again."}
            </Alert>
          )}
          {uploadStatus === "uploaded" && onAnnotateInstead && (
            <Stack
              direction={"row"}
              spacing={1.5}
              alignItems={"center"}
              sx={{
                p: 1.5,
                borderRadius: 1,
                backgroundColor: `${theme.palette.info.main}10`,
              }}
            >
              <AnnotateIcon color={"info"} />
              <Box sx={{ flex: 1 }}>
                <Typography variant={"body2"} fontWeight={600}>
                  {"Want to mark up the edited document?"}
                </Typography>
                <Typography variant={"caption"} color={"text.secondary"}>
                  {
                    "Open your uploaded file in the annotation canvas to drop field markers, highlights, or comments before submitting."
                  }
                </Typography>
              </Box>
              <Button
                variant={"outlined"}
                startIcon={<AnnotateIcon />}
                onClick={() =>
                  onAnnotateInstead({
                    file: editedFile,
                    s3FileId: editedFileId,
                    templateId: picked.templateId,
                    templateTitle: picked.templateTitle,
                    originalS3FileId: picked.s3FileId,
                  })
                }
              >
                {"Annotate this template"}
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>

      <Paper variant={"outlined"} sx={{ p: 2 }}>
        <Stack gap={2}>
          <Box>
            <Typography variant={"subtitle1"} fontWeight={600}>
              {"3. Describe the change"}
            </Typography>
          </Box>
          <TextField
            fullWidth
            label={"Template Name"}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            helperText={"What this template should be called once published"}
          />
          <TextField
            fullWidth
            multiline
            minRows={3}
            label={"Any additional comments"}
            placeholder={
              "Anything else we should know about the changes you have made..."
            }
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <TextField
            fullWidth
            select
            label={"Priority"}
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            SelectProps={{ native: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position={"start"}>
                  <PriorityIcon
                    sx={{
                      color:
                        priority === "HIGH"
                          ? "error.main"
                          : priority === "MEDIUM"
                          ? "warning.main"
                          : "success.main",
                    }}
                  />
                </InputAdornment>
              ),
            }}
          >
            <option value={"LOW"}>
              {"Low - No rush, whenever convenient"}
            </option>
            <option value={"MEDIUM"}>{"Medium - Standard turnaround"}</option>
            <option value={"HIGH"}>{"High - Urgent, needed soon"}</option>
          </TextField>
        </Stack>
      </Paper>

      {showMissingFileNudge && (
        <Alert
          severity={"warning"}
          action={
            <Button
              size={"small"}
              variant={"outlined"}
              onClick={() => {
                setShowMissingFileNudge(false);
                submit();
              }}
            >
              {"Submit anyway"}
            </Button>
          }
        >
          {
            "Looks like you have not uploaded an edited version yet. The support team will receive just your comments without a marked-up document."
          }
        </Alert>
      )}

      <Stack direction={"row"} justifyContent={"flex-end"} gap={1}>
        {onCancel && (
          <Button variant={"text"} onClick={onCancel}>
            {"Cancel"}
          </Button>
        )}
        <Button
          startIcon={<SubmitIcon />}
          onClick={handleSubmitClick}
          isLoading={submitting}
        >
          {"Submit Request"}
        </Button>
      </Stack>
    </Stack>
  );
};

export default EditExistingTemplateFlow;
